import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Crown, Music, Ticket, AlertTriangle, Users } from 'lucide-react';

import { API_URL } from '../utils/api';

export default function BingoDisplayPage() {
  const { gameCode } = useParams();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastDance, setLastDance] = useState(null);
  const [showFlash, setShowFlash] = useState(false);

  const fetchGame = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${gameCode}`);
      if (res.ok) {
        const data = await res.json();
        
        // Check if new dance was called
        const calledDances = data.called_dances || [];
        if (calledDances.length > 0) {
          const newLastDance = calledDances[calledDances.length - 1];
          if (newLastDance !== lastDance && lastDance !== null) {
            // New dance called - flash effect
            setShowFlash(true);
            setTimeout(() => setShowFlash(false), 1000);
          }
          setLastDance(newLastDance);
        }
        
        setGame(data);
        setError(null);
      } else {
        setError('Game not found');
      }
    } catch (err) {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  }, [gameCode, lastDance]);

  useEffect(() => {
    fetchGame();
    const interval = setInterval(fetchGame, 2000); // Poll every 2 seconds
    return () => clearInterval(interval);
  }, [fetchGame]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white text-2xl">Loading...</div>
      </div>
    );
  }

  if (error || !game) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-red-400 text-2xl">{error || 'Game not found'}</div>
      </div>
    );
  }

  const calledDances = game.called_dances || [];
  const leaderboard = game.leaderboard || [];
  const currentDance = calledDances.length > 0 ? calledDances[calledDances.length - 1] : null;
  const isPaused = game.status === 'paused';
  const paperWinners = leaderboard.filter(p => p.is_paper_ticket && p.best_col_progress === 5);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6 overflow-hidden">
      {/* Paused Alert */}
      {isPaused && (
        <div className="fixed top-0 left-0 right-0 bg-yellow-500 text-black py-4 px-6 text-center z-50 animate-pulse">
          <div className="flex items-center justify-center gap-3 text-2xl font-bold">
            <AlertTriangle className="w-8 h-8" />
            GAME PAUSED - POSSIBLE WINNER!
            <AlertTriangle className="w-8 h-8" />
          </div>
        </div>
      )}

      {/* Paper Winners Alert */}
      {paperWinners.length > 0 && !isPaused && (
        <div className="fixed top-0 left-0 right-0 bg-green-500 text-white py-3 px-6 text-center z-50">
          <div className="flex items-center justify-center gap-3 text-xl font-bold">
            <Crown className="w-6 h-6" />
            PAPER TICKET WINNER: {paperWinners.map(p => p.nickname.replace('🎫 ', '')).join(', ')}
            <Crown className="w-6 h-6" />
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold text-white mb-2">
            {game.win_pattern === 'ognib' ? 'OGNIB - Reverse Bingo' : 'Line Dance Bingo'}
          </h1>
          <div className="flex items-center justify-center gap-4 text-slate-400">
            <span>Game: {game.game_code}</span>
            <span>•</span>
            <span>Host: {game.host_name}</span>
            <span>•</span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              isPaused ? 'bg-yellow-500 text-black' :
              game.status === 'active' ? 'bg-green-500 text-white' : 'bg-slate-600 text-white'
            }`}>
              {isPaused ? 'PAUSED' : game.status?.toUpperCase()}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Current Dance - Large Display */}
          <div className="col-span-2">
            <div className={`bg-slate-800/80 rounded-2xl p-8 border-2 transition-all duration-300 ${
              showFlash ? 'border-yellow-400 bg-yellow-900/30' : 'border-purple-500/50'
            }`}>
              <div className="text-slate-400 text-lg mb-2 flex items-center gap-2">
                <Music className="w-5 h-5" />
                Current Dance
              </div>
              {currentDance ? (
                <div className={`text-6xl font-bold text-white transition-all duration-300 ${
                  showFlash ? 'scale-110 text-yellow-300' : ''
                }`}>
                  {currentDance}
                </div>
              ) : (
                <div className="text-4xl text-slate-500">Waiting for first dance...</div>
              )}
              <div className="mt-4 text-2xl text-purple-400">
                {calledDances.length} / {game.dance_pool?.length || '?'} dances called
              </div>
            </div>

            {/* Called Dances Grid */}
            <div className="mt-6 bg-slate-800/60 rounded-xl p-6 border border-slate-700">
              <div className="text-slate-400 text-lg mb-4">Called Dances</div>
              <div className="flex flex-wrap gap-2 max-h-[35vh] overflow-y-auto">
                {calledDances.map((dance, idx) => (
                  <span 
                    key={idx}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                      idx === calledDances.length - 1 
                        ? 'bg-purple-600 text-white ring-2 ring-purple-400' 
                        : 'bg-slate-700 text-slate-300'
                    }`}
                  >
                    {dance}
                  </span>
                ))}
                {calledDances.length === 0 && (
                  <span className="text-slate-500">No dances called yet</span>
                )}
              </div>
            </div>
          </div>

          {/* Leaderboard */}
          <div className="bg-slate-800/80 rounded-2xl p-6 border border-slate-700">
            <div className="text-slate-400 text-lg mb-4 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Live Leaderboard
            </div>
            
            <div className="space-y-2 max-h-[70vh] overflow-y-auto">
              {leaderboard.length > 0 ? (
                leaderboard.slice(0, 15).map((player, idx) => (
                  <div 
                    key={player.player_id}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      player.best_col_progress === 5
                        ? 'bg-green-600/30 border-2 border-green-500 animate-pulse'
                        : player.is_paper_ticket
                          ? 'bg-emerald-900/40 border border-emerald-600/50'
                          : idx === 0
                            ? 'bg-yellow-900/30 border border-yellow-500/50'
                            : 'bg-slate-700/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        player.best_col_progress === 5 ? 'bg-green-500 text-white' :
                        player.is_paper_ticket ? 'bg-emerald-600 text-white' :
                        idx === 0 ? 'bg-yellow-500 text-black' :
                        'bg-slate-600 text-white'
                      }`}>
                        {player.best_col_progress === 5 ? <Crown className="w-4 h-4" /> :
                         player.is_paper_ticket ? <Ticket className="w-4 h-4" /> : idx + 1}
                      </div>
                      <div>
                        <span className={`font-medium ${
                          player.is_paper_ticket ? 'text-emerald-300 font-mono' : 'text-white'
                        }`}>
                          {player.is_paper_ticket ? player.nickname.replace('🎫 ', '') : player.nickname}
                        </span>
                        {player.is_paper_ticket && (
                          <span className="text-emerald-500 text-xs ml-2">(paper)</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className={`text-lg font-bold ${
                        player.best_col_progress === 5 ? 'text-green-400' :
                        player.best_col_progress >= 4 ? 'text-yellow-400' : 'text-white'
                      }`}>
                        {player.best_col_progress}/5
                      </div>
                      <div className="w-16 h-2 bg-slate-600 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all ${
                            player.best_col_progress === 5 ? 'bg-green-500' :
                            player.best_col_progress >= 4 ? 'bg-yellow-500' : 'bg-purple-500'
                          }`}
                          style={{ width: `${(player.best_col_progress / 5) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  Waiting for players...
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="mt-4 pt-4 border-t border-slate-700 grid grid-cols-2 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-white">
                  {leaderboard.filter(p => !p.is_paper_ticket).length}
                </div>
                <div className="text-xs text-slate-400">Online Players</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-emerald-400">
                  {leaderboard.filter(p => p.is_paper_ticket).length}
                </div>
                <div className="text-xs text-slate-400">Paper Tickets</div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-slate-500 text-sm">
          Join at: {window.location.origin}/bingo/play/{game.game_code}
        </div>
      </div>
    </div>
  );
}
